import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ExclamationTriangleIcon,
  CubeIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from 'react-hot-toast';

export default function Itens() {
  const [itens, setItens] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('');
  const [filterEstoqueBaixo, setFilterEstoqueBaixo] = useState(false);
  
  // Estados da paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  
  // Estados do modal
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    codigo: '',
    nome: '',
    descricao: '',
    categoriaId: '',
    unidade: 'UN',
    estoqueMinimo: 0,
    estoqueAtual: 0,
    precoUnitario: 0,
    fornecedor: '',
    localizacao: '',
    ativo: true
  });
  const [referencias, setReferencias] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchItens();
    fetchCategorias();
  }, [search, filterCategoria, filterEstoqueBaixo, currentPage, itemsPerPage]);

  // Resetar para primeira página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterCategoria, filterEstoqueBaixo, itemsPerPage]);

  const fetchItens = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterCategoria) params.append('categoriaId', filterCategoria);
      if (filterEstoqueBaixo) params.append('estoqueBaixo', 'true');
      
      // Adicionar parâmetros de paginação
      params.append('page', currentPage.toString());
      params.append('limit', itemsPerPage.toString());

      const response = await api.get(`/itens?${params}`);
      setItens(response.data.itens);
      
      // Atualizar informações de paginação
      if (response.data.pagination) {
        setTotalPages(response.data.pagination.pages);
        setTotalItems(response.data.pagination.total);
      }
    } catch (error) {
      console.error('Erro ao carregar itens:', error);
      toast.error('Erro ao carregar itens');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategorias = async () => {
    try {
      const response = await api.get('/categorias?ativo=true');
      setCategorias(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const getEstoqueStatus = (item) => {
    if (item.estoqueAtual === 0) {
      return { text: 'Sem estoque', class: 'bg-red-100 text-red-800' };
    }
    if (item.estoqueAtual <= item.estoqueMinimo) {
      return { text: 'Estoque baixo', class: 'bg-yellow-100 text-yellow-800' };
    }
    return { text: 'Normal', class: 'bg-green-100 text-green-800' };
  };

  // Funções do modal
  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        codigo: item.codigo,
        nome: item.nome,
        descricao: item.descricao || '',
        categoriaId: item.categoriaId,
        unidade: item.unidade,
        estoqueMinimo: item.estoqueMinimo,
        estoqueAtual: item.estoqueAtual,
        precoUnitario: item.precoUnitario,
        fornecedor: item.fornecedor || '',
        localizacao: item.localizacao || '',
        ativo: item.ativo
      });
      setReferencias(item.referencias || []);
    } else {
      setEditingItem(null);
      setFormData({
        codigo: '',
        nome: '',
        descricao: '',
        categoriaId: '',
        unidade: 'UN',
        estoqueMinimo: 0,
        estoqueAtual: 0,
        precoUnitario: 0,
        fornecedor: '',
        localizacao: '',
        ativo: true
      });
      setReferencias([]);
    }
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({
      codigo: '',
      nome: '',
      descricao: '',
      categoriaId: '',
      unidade: 'UN',
      estoqueMinimo: 0,
      estoqueAtual: 0,
      precoUnitario: 0,
      fornecedor: '',
      localizacao: '',
      ativo: true
    });
    setReferencias([]);
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Limpar erro do campo quando usuário começa a digitar
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Funções para gerenciar referências
  const adicionarReferencia = () => {
    setReferencias(prev => [...prev, {
      id: Date.now().toString(), // ID temporário para novas referências
      codigo: '',
      fornecedor: '',
      barcode: '',
      observacao: ''
    }]);
  };

  const removerReferencia = (index) => {
    setReferencias(prev => prev.filter((_, i) => i !== index));
  };

  const atualizarReferencia = (index, campo, valor) => {
    setReferencias(prev => prev.map((ref, i) => 
      i === index ? { ...ref, [campo]: valor } : ref
    ));
  };

  const validateForm = () => {
    const errors = {};
    
    // Validar código apenas para edição
    if (editingItem && !formData.codigo.trim()) {
      errors.codigo = 'Código é obrigatório';
    }
    
    if (!formData.nome.trim()) {
      errors.nome = 'Nome é obrigatório';
    }
    
    if (!formData.categoriaId) {
      errors.categoriaId = 'Categoria é obrigatória';
    }
    
    if (formData.estoqueMinimo < 0) {
      errors.estoqueMinimo = 'Estoque mínimo não pode ser negativo';
    }
    
    if (formData.estoqueAtual < 0) {
      errors.estoqueAtual = 'Estoque atual não pode ser negativo';
    }
    
    if (formData.precoUnitario < 0) {
      errors.precoUnitario = 'Preço não pode ser negativo';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      const dataToSend = { ...formData, referencias };
      
      if (editingItem) {
        // Atualizar item
        await api.put(`/itens/${editingItem.id}`, dataToSend);
        toast.success('Item atualizado com sucesso!');
      } else {
        // Criar novo item - remover campo código do payload
        const { codigo, ...itemData } = dataToSend;
        await api.post('/itens', itemData);
        toast.success('Item criado com sucesso!');
      }
      
      closeModal();
      fetchItens();
    } catch (error) {
      console.error('Erro ao salvar item:', error);
      const message = error.response?.data?.error || 'Erro ao salvar item';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Tem certeza que deseja excluir o item "${item.nome}"?`)) {
      return;
    }
    
    try {
      await api.delete(`/itens/${item.id}`);
      toast.success('Item excluído com sucesso!');
      fetchItens();
    } catch (error) {
      console.error('Erro ao excluir item:', error);
      const message = error.response?.data?.error || 'Erro ao excluir item';
      toast.error(message);
    }
  };

  // Funções de navegação de páginas
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Gerar array de páginas para exibição
  const getPageNumbers = () => {
    if (totalPages <= 1) return [1];
    
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); 
         i <= Math.min(totalPages - 1, currentPage + delta); 
         i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots.filter((item, index, array) => array.indexOf(item) === index);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Título */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Itens</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerenciamento de itens do estoque
          </p>
        </div>
        <button 
          onClick={() => openModal()}
          className="btn-primary"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Novo Item
        </button>
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-5">
            {/* Busca */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  className="input pl-10"
                  placeholder="Código, nome ou descrição..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Filtro por categoria */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoria
              </label>
              <select
                className="input"
                value={filterCategoria}
                onChange={(e) => setFilterCategoria(e.target.value)}
              >
                <option value="">Todas as categorias</option>
                {categorias.map((categoria) => (
                  <option key={categoria.id} value={categoria.id}>
                    {categoria.nome}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro estoque baixo */}
            <div className="flex items-end">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  checked={filterEstoqueBaixo}
                  onChange={(e) => setFilterEstoqueBaixo(e.target.checked)}
                />
                <span className="ml-2 text-sm text-gray-700">Estoque baixo</span>
              </label>
            </div>

            {/* Itens por página */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Itens por página
              </label>
              <select
                className="input"
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
              >
                <option value={5}>5 itens</option>
                <option value={10}>10 itens</option>
                <option value={20}>20 itens</option>
                <option value={50}>50 itens</option>
                <option value={100}>100 itens</option>
              </select>
            </div>

            {/* Botão limpar filtros */}
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearch('');
                  setFilterCategoria('');
                  setFilterEstoqueBaixo(false);
                  setCurrentPage(1);
                }}
                className="btn-secondary"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Limpar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de itens */}
      <div className="card">
        <div className="card-body">
          {itens.length === 0 ? (
            <div className="text-center py-12">
              <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum item encontrado</h3>
              <p className="mt-1 text-sm text-gray-500">
                {search || filterCategoria || filterEstoqueBaixo
                  ? 'Tente ajustar os filtros de busca.'
                  : 'Comece adicionando um novo item.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Item
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Categoria
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estoque
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Preço
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {itens.map((item) => {
                    const estoqueStatus = getEstoqueStatus(item);
                    return (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {item.codigo}
                            </div>
                            <div className="text-sm text-gray-500">
                              {item.nome}
                            </div>
                            {item.descricao && (
                              <div className="text-xs text-gray-400 truncate max-w-xs">
                                {item.descricao}
                              </div>
                            )}
                            {item.referencias && item.referencias.length > 0 && (
                              <div className="mt-1 space-y-1">
                                {item.referencias.slice(0, 2).map((ref, idx) => (
                                  <div key={idx} className="text-xs text-blue-600">
                                    {ref.fornecedor}: {ref.codigo}
                                  </div>
                                ))}
                                {item.referencias.length > 2 && (
                                  <div className="text-xs text-gray-400">
                                    +{item.referencias.length - 2} mais
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div
                              className="w-3 h-3 rounded-full mr-2"
                              style={{ backgroundColor: item.categoria.cor }}
                            />
                            <span className="text-sm text-gray-900">
                              {item.categoria.nome}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {item.estoqueAtual} {item.unidade}
                          </div>
                          <div className="text-xs text-gray-500">
                            Mín: {item.estoqueMinimo} {item.unidade}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            R$ {item.precoUnitario.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${estoqueStatus.class}`}>
                            {estoqueStatus.text === 'Estoque baixo' && (
                              <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                            )}
                            {estoqueStatus.text}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button 
                            onClick={() => openModal(item)}
                            className="text-primary-600 hover:text-primary-900 mr-3"
                          >
                            <PencilIcon className="h-4 w-4 inline mr-1" />
                            Editar
                          </button>
                          <button 
                            onClick={() => handleDelete(item)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="h-4 w-4 inline mr-1" />
                            Excluir
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Informações de paginação e navegação */}
          {totalItems > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
              {/* Informações da paginação */}
              <div className="text-sm text-gray-700">
                Mostrando {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} até{' '}
                {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} itens
              </div>

              {/* Navegação de páginas */}
              {totalPages > 1 && (
                <div className="flex items-center space-x-2">
                  {/* Botão anterior */}
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className={`
                      px-3 py-2 rounded-md text-sm font-medium flex items-center
                      ${currentPage === 1 
                        ? 'text-gray-400 cursor-not-allowed' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }
                    `}
                  >
                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                    Anterior
                  </button>

                  {/* Números das páginas */}
                  <div className="flex space-x-1">
                    {getPageNumbers().map((page, index) => (
                      <button
                        key={index}
                        onClick={() => typeof page === 'number' ? handlePageChange(page) : null}
                        disabled={page === '...'}
                        className={`
                          px-3 py-2 rounded-md text-sm font-medium
                          ${page === currentPage
                            ? 'bg-primary-600 text-white'
                            : page === '...'
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                          }
                        `}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  {/* Botão próximo */}
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`
                      px-3 py-2 rounded-md text-sm font-medium flex items-center
                      ${currentPage === totalPages 
                        ? 'text-gray-400 cursor-not-allowed' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }
                    `}
                  >
                    Próximo
                    <ChevronRightIcon className="h-4 w-4 ml-1" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingItem ? 'Editar Item' : 'Novo Item'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Código - Apenas para edição */}
                {editingItem && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Código
                    </label>
                    <input
                      type="text"
                      name="codigo"
                      value={formData.codigo}
                      readOnly
                      className="input bg-gray-50 text-gray-500 cursor-not-allowed"
                      placeholder="Gerado automaticamente"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      O código é gerado automaticamente baseado na categoria
                    </p>
                  </div>
                )}

                {/* Nome */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome *
                  </label>
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    className={`input ${formErrors.nome ? 'border-red-500' : ''}`}
                    placeholder="Nome do item"
                  />
                  {formErrors.nome && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.nome}</p>
                  )}
                </div>

                {/* Categoria */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoria *
                  </label>
                  {editingItem ? (
                    <>
                      <input
                        type="text"
                        value={categorias.find(c => c.id === formData.categoriaId)?.nome || ''}
                        readOnly
                        className="input bg-gray-50 text-gray-500 cursor-not-allowed"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        <span className="font-medium">⚠️ Categoria não pode ser alterada após criação</span><br/>
                        Isso garante que o código do item permaneça consistente com o prefixo da categoria.
                      </p>
                    </>
                  ) : (
                    <>
                      <select
                        name="categoriaId"
                        value={formData.categoriaId}
                        onChange={handleInputChange}
                        className={`input ${formErrors.categoriaId ? 'border-red-500' : ''}`}
                      >
                        <option value="">Selecione uma categoria</option>
                        {categorias.map((categoria) => (
                          <option key={categoria.id} value={categoria.id}>
                            {categoria.nome}
                          </option>
                        ))}
                      </select>
                      {formErrors.categoriaId && (
                        <p className="text-red-500 text-xs mt-1">{formErrors.categoriaId}</p>
                      )}
                      {formData.categoriaId && (
                        <p className="text-xs text-gray-500 mt-1">
                          <span className="font-medium">Código será gerado:</span> {
                            categorias.find(c => c.id === formData.categoriaId)?.prefixo || ''
                          }0001 (exemplo)
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* Unidade */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unidade
                  </label>
                  <select
                    name="unidade"
                    value={formData.unidade}
                    onChange={handleInputChange}
                    className="input"
                  >
                    <option value="UN">Unidade (UN)</option>
                    <option value="KG">Quilograma (KG)</option>
                    <option value="L">Litro (L)</option>
                    <option value="M">Metro (M)</option>
                    <option value="PCT">Pacote (PCT)</option>
                    <option value="CX">Caixa (CX)</option>
                  </select>
                </div>

                {/* Estoque Mínimo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estoque Mínimo
                  </label>
                  <input
                    type="number"
                    name="estoqueMinimo"
                    value={formData.estoqueMinimo}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className={`input ${formErrors.estoqueMinimo ? 'border-red-500' : ''}`}
                  />
                  {formErrors.estoqueMinimo && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.estoqueMinimo}</p>
                  )}
                </div>

                {/* Estoque Atual */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estoque Atual
                  </label>
                  <input
                    type="number"
                    name="estoqueAtual"
                    value={formData.estoqueAtual}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className={`input ${formErrors.estoqueAtual ? 'border-red-500' : ''}`}
                  />
                  {formErrors.estoqueAtual && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.estoqueAtual}</p>
                  )}
                </div>

                {/* Preço Unitário */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preço Unitário (R$)
                  </label>
                  <input
                    type="number"
                    name="precoUnitario"
                    value={formData.precoUnitario}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className={`input ${formErrors.precoUnitario ? 'border-red-500' : ''}`}
                  />
                  {formErrors.precoUnitario && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.precoUnitario}</p>
                  )}
                </div>

                {/* Fornecedor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fornecedor
                  </label>
                  <input
                    type="text"
                    name="fornecedor"
                    value={formData.fornecedor}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="Nome do fornecedor"
                  />
                </div>

                {/* Localização */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Localização
                  </label>
                  <input
                    type="text"
                    name="localizacao"
                    value={formData.localizacao}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="Ex: Prateleira A1"
                  />
                </div>
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleInputChange}
                  rows="3"
                  className="input"
                  placeholder="Descrição detalhada do item..."
                />
              </div>

              {/* Referências */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Códigos de Referência
                  </label>
                  <button
                    type="button"
                    onClick={adicionarReferencia}
                    className="btn-secondary text-sm"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Adicionar Código
                  </button>
                </div>
                
                {referencias.length === 0 ? (
                  <div className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-md">
                    Nenhum código de referência adicionado
                  </div>
                ) : (
                  <div className="space-y-3">
                    {referencias.map((ref, index) => (
                      <div key={index} className="border rounded-md p-3 bg-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-sm font-medium text-gray-700">
                            Referência {index + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => removerReferencia(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Código
                            </label>
                            <input
                              type="text"
                              value={ref.codigo}
                              onChange={(e) => atualizarReferencia(index, 'codigo', e.target.value)}
                              className="input text-sm"
                              placeholder="Código do fornecedor"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Fornecedor
                            </label>
                            <input
                              type="text"
                              value={ref.fornecedor}
                              onChange={(e) => atualizarReferencia(index, 'fornecedor', e.target.value)}
                              className="input text-sm"
                              placeholder="Nome do fornecedor"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Código de Barras
                            </label>
                            <input
                              type="text"
                              value={ref.barcode || ''}
                              onChange={(e) => atualizarReferencia(index, 'barcode', e.target.value)}
                              className="input text-sm"
                              placeholder="Código de barras"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Observação
                            </label>
                            <input
                              type="text"
                              value={ref.observacao || ''}
                              onChange={(e) => atualizarReferencia(index, 'observacao', e.target.value)}
                              className="input text-sm"
                              placeholder="Observações"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Ativo */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="ativo"
                  checked={formData.ativo}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Item ativo
                </label>
              </div>

              {/* Botões */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary"
                  disabled={submitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Salvando...' : (editingItem ? 'Atualizar' : 'Criar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
} 